import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Plus, Circle, Clock, AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "../../lib/api";

export default function Taches() {
  const { t } = useTranslation();
  const [tasks, setTasks] = useState(null);
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");

  const load = () => api.get("/tasks").then((r) => setTasks(r.data.tasks)).catch(() => {});
  useEffect(() => { load(); }, []);

  const today = new Date().toISOString().slice(0, 10);
  const groups = useMemo(() => {
    const g = { late: [], today: [], upcoming: [], done: [] };
    (tasks || []).forEach((task) => {
      if (task.status === "done") g.done.push(task);
      else if (task.due < today) g.late.push(task);
      else if (task.due === today) g.today.push(task);
      else g.upcoming.push(task);
    });
    return g;
  }, [tasks, today]);

  const add = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    try {
      await api.post("/tasks", { title, due: due || today });
      setTitle(""); setDue("");
      toast.success(t("taches.added"));
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const toggle = async (t) => {
    setTasks(tasks.map((x) => x.task_id === t.task_id ? { ...x, status: x.status === "done" ? "todo" : "done" } : x));
    await api.patch(`/tasks/${t.task_id}`, { status: t.status === "done" ? "todo" : "done" });
  };

  const remove = async (t) => {
    await api.delete(`/tasks/${t.task_id}`);
    load();
  };

  const Section = ({ titleKey, items, icon: Icon, color }) => items.length > 0 && (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={15} className={color} />
        <h2 className="font-display font-bold text-sm uppercase tracking-wider">{t(`taches.sections.${titleKey}`)}</h2>
        <span className="text-[10px] font-bold bg-secondary rounded-full px-2 py-0.5">{items.length}</span>
      </div>
      <div className="space-y-2">
        {items.map((task, i) => (
          <motion.div key={task.task_id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
            className="group flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3.5"
            data-testid={`task-${task.task_id}`}>
            <button onClick={() => toggle(task)} data-testid={`task-toggle-${task.task_id}`}
              className="shrink-0 text-muted-foreground hover:text-success transition-colors">
              {task.status === "done" ? <CheckCircle2 size={19} className="text-success" /> : <Circle size={19} />}
            </button>
            <div className="min-w-0 flex-1">
              <p className={`text-sm font-medium ${task.status === "done" ? "line-through text-muted-foreground" : ""}`}>{task.title}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {t("taches.due", { date: task.due })}{task.linked ? ` · ${task.linked}` : ""}
              </p>
            </div>
            <button onClick={() => remove(task)} data-testid={`task-delete-${task.task_id}`}
              className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg text-muted-foreground hover:text-destructive transition-all">
              <Trash2 size={14} />
            </button>
          </motion.div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-8 max-w-3xl" data-testid="taches-page">
      <div>
        <h1 className="font-display font-black text-3xl tracking-tighter">{t("taches.title")}</h1>
        <p className="text-muted-foreground text-sm mt-1">{t("taches.subtitle")}</p>
      </div>

      <form onSubmit={add} className="flex gap-2" data-testid="new-task-form">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("taches.newPlaceholder")}
          data-testid="new-task-title"
          className="flex-1 rounded-xl border border-border bg-card px-4 py-3 text-sm outline-none focus:border-electric focus:ring-2 focus:ring-electric/20 transition-shadow" />
        <input type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="new-task-due"
          className="rounded-xl border border-border bg-card px-4 py-3 text-sm outline-none focus:border-electric" />
        <button type="submit" data-testid="new-task-submit"
          className="rounded-full bg-electric text-white px-5 py-3 text-xs font-bold hover:bg-blue-700 transition-colors flex items-center gap-1.5">
          <Plus size={14} /> {t("taches.add")}
        </button>
      </form>

      {!tasks ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="skeleton-shimmer h-14 rounded-xl" />)}</div>
      ) : tasks.length === 0 ? (
        <div className="rounded-2xl border border-border bg-card py-20 text-center" data-testid="taches-empty">
          <CheckCircle2 size={32} className="mx-auto text-success mb-4" />
          <p className="font-semibold text-sm">{t("taches.empty1")}</p>
          <p className="text-xs text-muted-foreground mt-1">{t("taches.empty2")}</p>
        </div>
      ) : (
        <>
          <Section titleKey="late" items={groups.late} icon={AlertTriangle} color="text-destructive" />
          <Section titleKey="today" items={groups.today} icon={Clock} color="text-electric" />
          <Section titleKey="upcoming" items={groups.upcoming} icon={Circle} color="text-muted-foreground" />
          <Section titleKey="done" items={groups.done} icon={CheckCircle2} color="text-success" />
        </>
      )}
    </div>
  );
}
